import {
  ClientEvent,
  ConditionKind,
  IPushRule,
  MatrixClient,
  MatrixEvent,
  PushRuleKind,
} from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import soundMuteStore from 'hooks/useSoundMutedRoomIds/soundMuteStore';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

const soundMuteRuleId = (roomId: string): string => `${CustomMeedioMatrixEvents.SOUND_MUTE_RULE_PREFIX}.${roomId}`;

const findSoundMuteRule = (matrixClient: MatrixClient, roomId: string): IPushRule | undefined => {
  const overrides = matrixClient.pushRules?.global?.override ?? [];
  return overrides.find((r) => r.rule_id === soundMuteRuleId(roomId));
};

const getIsSoundMuted = (matrixClient: MatrixClient, roomId: string): boolean => {
  const rule = findSoundMuteRule(matrixClient, roomId);
  return !!rule && rule.enabled !== false;
};

interface UseMessengerRoomSoundMuteReturn {
  isSoundMuted: boolean;
  isLoading: boolean;
  toggleSoundMute: () => Promise<void>;
}

const useMessengerRoomSoundMute = (roomId: string): UseMessengerRoomSoundMuteReturn => {
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { t } = useTranslation();
  const [isSoundMuted, setIsSoundMuted] = useState(() => getIsSoundMuted(matrixClient, roomId));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsSoundMuted(getIsSoundMuted(matrixClient, roomId));
    const handleAccountData = (event: MatrixEvent) => {
      if (event.getType() !== 'm.push_rules') return;
      setIsSoundMuted(getIsSoundMuted(matrixClient, roomId));
    };
    matrixClient.on(ClientEvent.AccountData, handleAccountData);
    return () => {
      matrixClient.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [matrixClient, roomId]);

  const toggleSoundMute = useCallback(async () => {
    if (isLoading) return;
    const wasMuted = isSoundMuted;
    setIsLoading(true);
    try {
      const existing = findSoundMuteRule(matrixClient, roomId);
      const ruleId = soundMuteRuleId(roomId);
      if (wasMuted) {
        if (existing) {
          await matrixClient.setPushRuleEnabled('global', PushRuleKind.Override, ruleId, false);
        }
      } else if (existing) {
        await matrixClient.setPushRuleEnabled('global', PushRuleKind.Override, ruleId, true);
      } else {
        await matrixClient.addPushRule('global', PushRuleKind.Override, ruleId, {
          actions: [],
          conditions: [{ kind: ConditionKind.EventMatch, key: 'room_id', pattern: roomId }],
        });
      }
      setIsSoundMuted(!wasMuted);
      soundMuteStore.setMuted(roomId, !wasMuted);
    } catch (error) {
      logger.error('useMessengerRoomSoundMute: toggle failed', { roomId, error });
      pushToast({
        variant: 'error',
        title: t(wasMuted ? 'messenger.sound_unmute_failed' : 'messenger.sound_mute_failed'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isSoundMuted, matrixClient, roomId, pushToast, t]);

  return { isSoundMuted, isLoading, toggleSoundMute };
};

export const getSoundMutedRoomIds = (matrixClient: MatrixClient): Set<string> => {
  const overrides = matrixClient.pushRules?.global?.override ?? [];
  const mutedIds = new Set<string>();
  overrides.forEach((rule) => {
    if (rule.enabled === false) return;
    const ruleId = rule.rule_id;
    if (!ruleId) return;

    const prefix = CustomMeedioMatrixEvents.SOUND_MUTE_RULE_PREFIX;
    if (ruleId.startsWith(`${prefix}.`)) {
      mutedIds.add(ruleId.slice(prefix.length + 1));
      return;
    }

    if (ruleId.startsWith('!') && Array.isArray(rule.actions) && rule.actions.length === 0) {
      mutedIds.add(ruleId);
    }
  });
  return mutedIds;
};

export default useMessengerRoomSoundMute;
